/** Reserved code for a `select_with_freetext` value the user typed in. */
export const FREETEXT_CODE = 'other';
/** The stable index for an option: its `code`, or its `value` as fallback. */
export function optionCode(option) {
    return option.code ?? option.value;
}
function options(field) {
    return field.options ?? [];
}
export class UnknownCodeError extends Error {
    fieldKey;
    code;
    constructor(fieldKey, code) {
        super(`Unknown option code "${code}" for field "${fieldKey}"`);
        this.fieldKey = fieldKey;
        this.code = code;
        this.name = 'UnknownCodeError';
    }
}
/** True when `code` resolves to a real option on this field. */
export function isKnownCode(field, code) {
    if (code === FREETEXT_CODE)
        return field.type === 'select_with_freetext';
    return options(field).some((o) => optionCode(o) === code);
}
/**
 * value → code for `select` / `select_with_freetext`. A stored value that
 * matches no predefined option is treated as free text (the
 * select_with_freetext convention; for a plain `select` it is an anomaly
 * we still preserve rather than drop).
 */
export function serializeChoice(field, stored) {
    const value = typeof stored === 'string' ? stored : '';
    if (value === '')
        return { code: null, freetext: null };
    const opt = options(field).find((o) => o.value === value);
    if (opt)
        return { code: optionCode(opt), freetext: null };
    return { code: FREETEXT_CODE, freetext: value };
}
/** value[] → code[] for `multi_select` (a closed set — no free text). */
export function serializeMultiChoice(field, stored) {
    const arr = Array.isArray(stored)
        ? stored.filter((x) => typeof x === 'string')
        : [];
    const codes = [];
    const unknownValues = [];
    for (const v of arr) {
        const opt = options(field).find((o) => o.value === v);
        if (opt)
            codes.push(optionCode(opt));
        else
            unknownValues.push(v);
    }
    return { codes, unknownValues };
}
/**
 * code → value for a single-choice field (the write boundary). Throws
 * `UnknownCodeError` on an unrecognised code so a strict (programmatic)
 * caller can reject with 400; a lenient (extraction) caller catches it and
 * falls back to free text. `FREETEXT_CODE` resolves to the given freetext.
 */
export function resolveCodeToValue(field, code, freetext) {
    if (code === FREETEXT_CODE) {
        if (field.type !== 'select_with_freetext') {
            throw new UnknownCodeError(field.key, code);
        }
        return freetext ?? '';
    }
    const opt = options(field).find((o) => optionCode(o) === code);
    if (!opt)
        throw new UnknownCodeError(field.key, code);
    return opt.value;
}
/** code[] → value[] for a `multi_select` field. Throws on any unknown code. */
export function resolveCodesToValues(field, codes) {
    return codes.map((c) => {
        const opt = options(field).find((o) => optionCode(o) === c);
        if (!opt)
            throw new UnknownCodeError(field.key, c);
        return opt.value;
    });
}
//# sourceMappingURL=codes.js.map